// src/components/Search/LinksDisplay.js

import React, { useState, useRef, useEffect } from "react";
import { FaTimes } from "react-icons/fa";

export default function LinksDisplay({ links }) {
  const [selectedLink, setSelectedLink] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

// Whenever `links` changes (new season/episode fetched), clear the player
 useEffect(() => {
    // This ensures we start fresh each time new links come in.
     setSelectedLink(null);
     setIsLoading(true);
   }, [links]);

  const handleLinkClick = (link) => {
    setSelectedLink(link.watchUrl);
    setIsLoading(true);
  };

  const handleCloseIframe = () => {
    setSelectedLink(null);
  };

  // Scroll to the iframe when it appears
  useEffect(() => {
    if (selectedLink && iframeRef.current) {
      iframeRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedLink]);

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  return (
    <div className="p-4 sm:p-6 min-h-screen text-white">
      <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Available Links</h3>
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((link, index) => (
          <div
            key={index}
            className="bg-gray-800 p-4 sm:p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
          >
            <button
              onClick={() => handleLinkClick(link)}
              className={`text-blue-400 break-all w-full p-3 sm:p-4 rounded-lg transition-all duration-300 transform ${
                selectedLink === link.watchUrl
                  ? "bg-gradient-to-r from-accentBlue to-accentBlueDark text-white shadow-lg ring-4 ring-accentBlue scale-105"
                  : "hover:scale-105"
              }`}
              aria-pressed={selectedLink === link.watchUrl}
            >
              <h4 className="text-lg sm:text-xl font-medium text-current mb-2 sm:mb-3">
                {link.source}
              </h4>
            </button>
          </div>
        ))}
      </div>

      {selectedLink && (
        <div
          ref={iframeRef}
          className="mt-6 sm:mt-8 w-full max-w-5xl mx-auto relative"
        >
          <div className="relative w-full h-0 pb-[56.25%]"> {/* 16:9 Aspect Ratio */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <svg
                  className="animate-spin h-10 w-10 sm:h-12 sm:w-12 text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
              </div>
            )}
            <iframe
              src={selectedLink}
              title="Video Player"
              className={`absolute top-0 left-0 w-full h-full rounded-md shadow-lg ${
                isLoading ? "hidden" : "block"
              }`}
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              onLoad={handleIframeLoad}
            ></iframe>
          </div>
          <button
            onClick={handleCloseIframe}
            className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-red-500 text-white p-2 sm:p-3 rounded-full hover:bg-red-600 transition-colors shadow-lg"
            title="Close Player"
          >
            <FaTimes className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
